/**
 * Toast (components.md 11)
 *
 * A white capsule at the bottom centre, 8 above the bottom edge or the
 * phone's tab bar, 16 in from the page's sides. Subhead 15 in the label
 * colour, no icon and no colour coding: the words say what happened and
 * what to do. A tap dismisses it. It stays two seconds at least, one more
 * per 20 characters past 40 (utils/toastDuration). The shadow is QA's
 * proposed reading of native's solid glass surface (status/decisions.md).
 */

import { useEffect, useState, useRef } from 'react';
import { toastDuration } from '../utils/toastDuration';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onClose: (id: string) => void;
}

function Toast({ toast, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose(toast.id);
    }, 200);
  };

  useEffect(() => {
    const fadeInTimer = setTimeout(() => setIsVisible(true), 10);
    timerRef.current = setTimeout(() => {
      handleClose();
    }, toastDuration(toast.message, toast.duration));

    return () => {
      clearTimeout(fadeInTimer);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-toast={toast.type}
      // role=alert for every kind, as before: e2e specs outside these screens read success toasts by it.
      role="alert"
      onClick={handleClose}
      className={
        'pointer-events-auto max-w-[480px] cursor-pointer rounded-[100px] bg-white px-4 py-3 ' +
        'text-center text-[15px] leading-[20px] text-db-text-1 [overflow-wrap:anywhere] ' +
        'shadow-[0_4px_16px_rgba(0,0,0,0.12)] transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ' +
        (isVisible && !isLeaving ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0')
      }
    >
      {toast.message}
    </div>
  );
}

interface ToastContainerProps {
  toasts: ToastMessage[];
  onClose: (id: string) => void;
}

/** The phone tab bar's height while it shows, so the toast sits 8 above it rather than under it. */
function tabBarInset(): number {
  const nav = document.getElementById('mobile-bottom-nav');
  // The bar is position: fixed, so offsetParent is always null; its computed display says whether it shows.
  return nav && getComputedStyle(nav).display !== 'none' ? nav.getBoundingClientRect().height : 0;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  const tabBar = toasts.length ? tabBarInset() : 0;
  return (
    <div
      className="pointer-events-none fixed inset-x-4 flex flex-col items-center gap-2"
      style={{ zIndex: 99999, bottom: tabBar ? tabBar + 8 : 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
    >
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onClose={onClose} />
      ))}
    </div>
  );
}
